import { useCallback, useEffect, useRef, useState } from 'react';

// BOU-1115: when multiple browser tabs are open to the same origin, each
// MusicProvider would otherwise play background music independently. The
// Web Locks API (per-origin, per-browser, auto-released on tab close) lets
// us elect a single primary tab. Non-primary tabs report isPrimary=false;
// MusicProvider routes that into its existing paused-state machinery.
//
// Fallback: in environments without navigator.locks (very old browsers,
// SSR, jsdom without a stub) the hook returns isPrimary=true so behavior
// is unchanged from before this hook existed.

const LOCK_NAME = 'gaia.music.primary';

const hasLocks = () =>
  typeof navigator !== 'undefined' && !!navigator.locks?.request;

const isHidden = () =>
  typeof document !== 'undefined' && document.visibilityState === 'hidden';

export default function useMusicPrimaryTab() {
  const supported = hasLocks();
  // Default true when unsupported (no coordination, everyone plays — matches
  // pre-BOU-1115 behavior). Default false when supported so audio stays
  // paused until the browser grants the lock.
  const [isPrimary, setIsPrimary] = useState(!supported);
  // Resolver of the *currently held* lock callback's Promise. Calling it
  // releases the lock. Catch handlers compare against this by identity so
  // a stale rejection (older steal that lost the race to a newer one)
  // cannot clobber the newer holder's state.
  const releaseHoldRef = useRef(null);
  // Guards against stacking multiple non-steal requests when the lock is
  // lost in rapid succession.
  const passiveQueuedRef = useRef(false);

  const releaseHold = useCallback(() => {
    if (releaseHoldRef.current) {
      releaseHoldRef.current();
      releaseHoldRef.current = null;
    }
  }, []);

  // Queue a non-steal request so we re-acquire the lock when the current
  // holder releases (e.g. closes their tab). Without this, losing the lock
  // would leave us non-primary indefinitely if no focus/visibility event
  // ever fires on this tab — browsers usually transfer focus when the
  // active tab closes, but Playwright-driven tabs and some window-manager
  // setups don't. Idempotent via passiveQueuedRef.
  const watchForRelease = useCallback((signal) => {
    if (!hasLocks() || passiveQueuedRef.current || signal?.aborted) return;
    passiveQueuedRef.current = true;
    let myResolver = null;
    navigator.locks
      .request(LOCK_NAME, { signal }, () => new Promise((resolve) => {
        passiveQueuedRef.current = false;
        // Don't promote a hidden tab — that would resume audio in a
        // background tab, contradicting focused-tab ownership.
        if (signal?.aborted || isHidden()) {
          resolve();
          return;
        }
        myResolver = resolve;
        releaseHoldRef.current = resolve;
        setIsPrimary(true);
      }))
      .catch(() => {
        passiveQueuedRef.current = false;
        if (signal?.aborted) return;
        // Only clear shared state if this request still owns the hold —
        // a newer acquire may have overwritten releaseHoldRef.
        if (releaseHoldRef.current === myResolver) {
          releaseHoldRef.current = null;
          setIsPrimary(false);
        }
      });
  }, []);

  const acquire = useCallback((signal) => {
    if (!hasLocks() || signal?.aborted) return;
    // Don't steal from the focused tab when this tab is hidden (background
    // mount, prerender). Queue a passive request so we'll get the lock
    // when the current focused tab releases.
    if (isHidden()) {
      watchForRelease(signal);
      return;
    }
    // Drop any prior hold first so the new steal request can take the lock
    // cleanly when the previous holder is aborted.
    releaseHold();
    let myResolver = null;
    // steal:true is incompatible with signal per spec, so we gate inside
    // the callback / catch on signal.aborted instead.
    navigator.locks
      .request(LOCK_NAME, { steal: true }, () => new Promise((resolve) => {
        if (signal?.aborted || isHidden()) {
          resolve();
          return;
        }
        myResolver = resolve;
        releaseHoldRef.current = resolve;
        setIsPrimary(true);
      }))
      .catch(() => {
        if (signal?.aborted) return;
        // Only clobber shared state if we still own the hold. A stale
        // steal rejected after a newer request has taken ownership must
        // not wipe the newer resolver.
        if (releaseHoldRef.current === myResolver) {
          releaseHoldRef.current = null;
          setIsPrimary(false);
        }
        // Queue a passive request so we pick the lock back up when the new
        // holder releases (e.g. closes their tab) without needing a focus
        // event to fire on us.
        watchForRelease(signal);
      });
  }, [releaseHold, watchForRelease]);

  useEffect(() => {
    if (!supported) return undefined;
    const ac = new AbortController();
    acquire(ac.signal);

    const onVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState === 'visible') {
        acquire(ac.signal);
      } else {
        releaseHold();
        setIsPrimary(false);
      }
    };
    const onFocus = () => acquire(ac.signal);

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      releaseHold();
      setIsPrimary(false);
      // Cancel pending passive requests so a late grant after unmount
      // doesn't silently hold the lock forever (no owner to release it).
      ac.abort();
    };
  }, [supported, acquire, releaseHold]);

  return { isPrimary };
}
